// M30ProductFeatures.tsx
import styles from './M30ProductFeatures.module.scss';
import classNames from 'classnames';
import { Button, ConsentPlaceholder, Heading, M30ProductFeaturesCard, M30ProductFeaturesCardProps, Modal, Paragraph, Picture } from 'ui/components';
import { useConsent, YOUTUBE_REQUIRED_GROUPS } from 'application/adapters/context/ConsentContext';
import { useEffect, useState } from 'react';
import { Icon } from 'ui/components/1-atoms/Media/Icon';
import IconPlay from 'ui/icons/icon_play.svg';
import { isYouTube, getYouTubeEmbedUrl } from 'helpers/youtubeEmbed';
import { SurfaceSection } from 'ui/components/4-habitats/SurfaceSection/SurfaceSection';

export interface M30ProductFeaturesProps {
	className?: string;
	heading?: string;
	products?: M30ProductFeaturesCardProps[];
	backgroundColorKey?: string | null;
	anchor?: string;
	consentHeading: string;
	consentText: string;
}

export const M30ProductFeatures: React.FC<M30ProductFeaturesProps> = ({
	className,
	heading,
	products,
	backgroundColorKey,
	anchor,
	consentHeading,
	consentText,
}) => {
	// map Modal to card props
	const [isProductModalOpen, setIsProductModalOpen] = useState(false);
	const [selectedFeature, setSelectedFeature] = useState<M30ProductFeaturesCardProps | null>(null);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const hasYouTubeConsent = useConsent(YOUTUBE_REQUIRED_GROUPS);

	// prevent initial click-through
	const [modalInteractive, setModalInteractive] = useState(false);

	const openProductModal = (feature: M30ProductFeaturesCardProps) => {
		setSelectedFeature(feature);
		setIsVideoPlaying(false);
		setIsProductModalOpen(true);
		setModalInteractive(false);
	};

	const closeProductModal = () => {
		setIsProductModalOpen(false);
		setIsVideoPlaying(false);
		setSelectedFeature(null);
		setModalInteractive(false);
	};

	// Disable body scroll when modal is open
	useEffect(() => {
		if (isProductModalOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isProductModalOpen]);

	useEffect(() => {
		if (isProductModalOpen) {
			const id = requestAnimationFrame(() => setModalInteractive(true));
			return () => cancelAnimationFrame(id);
		}
	}, [isProductModalOpen]);

	// Reset when closed, new video or consent changes, so a re-granted consent doesn't autoplay
	useEffect(() => {
		setIsVideoPlaying(false);
	}, [selectedFeature?.text, hasYouTubeConsent]);

	// Render modal media: image, consent placeholder, YouTube iframe or poster with play button
	const renderModalMedia = (feature: M30ProductFeaturesCardProps) => {
		const contentUrl = feature.youtubeLink;
		const image = feature.image?.url && (
			<Picture url={feature.image.url} aspectRatio={0.75} className={styles.M30ProductFeatures_ProductModalImage} />
		);

		if (!isYouTube(contentUrl)) return image;

		if (!hasYouTubeConsent) return <ConsentPlaceholder heading={consentHeading} text={consentText} />;

		if (isVideoPlaying) {
			return (
				<iframe
					className={styles.M30ProductFeatures_ModalPlayIframe}
					src={getYouTubeEmbedUrl(contentUrl) || undefined}
					title={feature.text}
					frameBorder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
				/>
			);
		}

		return (
			<Button
				type="button"
				className={styles.M30ProductFeatures_ModalPosterButton}
				onClick={() => modalInteractive && setIsVideoPlaying(true)}
				aria-label="Play video"
				disabled={!modalInteractive}
			>
				{image}
				<Icon className={classNames(styles.M30ProductFeatures_ModalPlayIcon)} size="lg">
					<IconPlay />
				</Icon>
			</Button>
		);
	};

	return (
		<SurfaceSection surfaceKey={backgroundColorKey} withContainer containerWidth="StandardWeb" as="section">
			<section className={classNames(styles.M30ProductFeatures, className)} id={anchor}>
				{heading && (
					<Heading headingLevel="h2" className={styles.M30ProductFeatures_Heading}>
						{heading}
					</Heading>
				)}

				{isProductModalOpen && selectedFeature && (
					<Modal
						modalType="M30ProductFeatures_Modal"
						title={selectedFeature.text}
						ariaClose="close"
						handleClose={closeProductModal}
						className={classNames(styles.M30ProductFeatures_ModalWrapper, className)}
					>
						<div className={classNames(styles.M30ProductFeatures_ModalContent, className)}>
							{renderModalMedia(selectedFeature)}
							<div className={styles.M30ProductFeatures_ModalContentText}>
								{selectedFeature?.title && (
									<Heading headingLevel="h3" className={styles.M30ProductFeatures_ModalTitle}>
										{selectedFeature?.title}
									</Heading>
								)}
								{selectedFeature.text && (
									<Paragraph className={styles.M30ProductFeatures_ModalDescription} variant="website">
										{selectedFeature?.text}
									</Paragraph>
								)}
							</div>
						</div>
					</Modal>
				)}

				<div className={classNames(styles.M30ProductFeatures_CardItems, className)}>
					{products?.map((product, idx) => (
						<Button
							key={`${product?.text ?? 'feature'}-${idx}`}
							className={styles.M30ProductFeatures_CardItemsButton}
							onClick={(e) => {
								e.stopPropagation();
								openProductModal(product);
							}}
							aria-label={`Open details for ${product?.text ?? 'feature'}`}
						>
							<M30ProductFeaturesCard className={styles.M30ProductFeatures_CardItemsButtonIcon} {...product} />
						</Button>
					))}
				</div>
			</section>
		</SurfaceSection>
	);
};
